/**
 * Reading audit events for display: where each one links, and its tone.
 *
 * Shared by the dashboard's activity feed and the notification center, so an
 * event looks — and links — the same in both.
 *
 * The words are looked up when called (never at import), in the
 * `administration` namespace, each with its English as the fallback — so a
 * caller whose screen has not loaded that namespace still reads English.
 */
import i18n from '../i18n/index.js';
import { humaniseAction } from './format.js';

/** One administration-namespace string, with its English as the fallback. */
function word(key, fallback) {
  return i18n.t(`administration:${key}`, { defaultValue: fallback });
}

/**
 * A translated phrase for a code, or the fallback. `returnObjects` lets a code
 * that is only a PREFIX of known codes ("crm.pipeline") be told apart from a
 * phrase: it resolves to an object, and the fallback is used instead.
 */
function phrase(key, fallback) {
  const value = i18n.t(`administration:${key}`, { defaultValue: fallback, returnObjects: true });
  return typeof value === 'string' && value ? value : fallback;
}

/**
 * An audit action code in words: "purchase_order.approved" -> "Purchase order
 * approved". A code without a translation is humanised, as before.
 */
export function activityActionLabel(action) {
  if (!action) return '';
  return phrase(`activity.actions.${action}`, humaniseAction(action));
}

/**
 * An audit record type in words: "PurchaseOrder" -> "Purchase order".
 * `fallback` is what an unknown type shows (by default, the type spaced out).
 */
export function activityEntityLabel(entity, fallback = undefined) {
  if (!entity) return '';
  const shown = fallback ?? String(entity).replace(/([A-Z])/g, ' $1').trim();
  return phrase(`activity.entities.${entity}`, shown);
}

/** Maps an audit action to a status tone, so a feed is scannable. */
export function activityTone(action = '') {
  if (action.includes('reject') || action.includes('cancel') || action.includes('delete')) return 'danger';
  if (action.includes('approve') || action.includes('receive')) return 'success';
  if (action.includes('submit') || action.includes('sent')) return 'info';
  return '';
}

/** Links an audit row back to the record it describes, where we can. */
export function activityLink(event) {
  if (!event?.entityId) return null;
  switch (event.entity) {
    case 'PurchaseRequest':
      return `/purchase-requests/${event.entityId}`;
    case 'PurchaseOrder':
      return `/purchase-orders/${event.entityId}`;
    case 'GoodsReceipt':
      return `/goods-receipts/${event.entityId}`;
    case 'Supplier':
      return `/suppliers/${event.entityId}`;
    default:
      return null;
  }
}

/**
 * Who acted, as a feed line names them: "You" for your own events, the
 * person's name otherwise. An event with no user was automated ("System");
 * one whose user can no longer be looked up — removed from the platform —
 * is "Former user", never a blank.
 */
export function actorLabel(event, selfId) {
  if (selfId && event?.userId === selfId) return word('activity.actor.you', 'You');
  if (event?.actor) return event.actor.fullName || event.actor.email || word('activity.actor.unknownUser', 'Unknown user');
  return event?.userId ? word('activity.actor.formerUser', 'Former user') : word('activity.actor.system', 'System');
}

/**
 * Whether NexERP's own staff made this change — creating the organization,
 * switching a module on. Those events belong in the organization's trail,
 * but must never read as though a colleague made them.
 */
export const isPlatformActor = (event) => Boolean(event?.actor?.isPlatformAdmin);

/** The activity log, opened on the same people a feed was showing. */
export const activityPageFor = (scope) => (scope === 'all' ? '/activity' : '/activity?people=me');

/** "Today", "Yesterday" or "Earlier", for grouping a feed by day. */
export function dayBucket(value, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return word('activity.day.earlier', 'Earlier');
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const time = date.getTime();
  if (time >= startOfToday) return word('activity.day.today', 'Today');
  if (time >= startOfToday - 86_400_000) return word('activity.day.yesterday', 'Yesterday');
  return word('activity.day.earlier', 'Earlier');
}
